import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Refresh, Delete, Edit, ChevronLeft, ChevronRight, CheckCircle } from '@mui/icons-material';
import {
  getWarehouseAddresses,
  createWarehouseAddress,
  updateWarehouseAddress,
  deleteWarehouseAddress,
  setActiveWarehouseAddress
} from '../../services/fulfillment';

const WarehouseAddress = () => {
  const { t } = useTranslation(['fulfillment', 'common']);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    receiver_name: '',
    province: '',
    city: '',
    address: '',
    phone: '',
    postcode: '',
    is_active: false
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      const response = await getWarehouseAddresses(params);
      if (response.results) {
        setAddresses(response.results);
        setTotalItems(response.count || 0);
      } else if (Array.isArray(response)) {
        setAddresses(response);
        setTotalItems(response.length);
      }
    } catch (error) {
      console.error('Error loading warehouse addresses:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:warehouseAddress.messages.failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.sid);
      setForm({
        receiver_name: item.receiver_name || '',
        province: item.province || '',
        city: item.city || '',
        address: item.address || '',
        phone: item.phone || '',
        postcode: item.postcode || '',
        is_active: item.is_active || false
      });
    } else {
      setEditId(null);
      setForm({
        receiver_name: '',
        province: '',
        city: '',
        address: '',
        phone: '',
        postcode: '',
        is_active: false
      });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({
      receiver_name: '',
      province: '',
      city: '',
      address: '',
      phone: '',
      postcode: '',
      is_active: false
    });
  };

  const handleSave = async () => {
    if (!form.receiver_name || !form.address || !form.phone || !form.postcode) {
      setSnackbar({
        open: true,
        message: t('common:validation.required'),
        severity: 'error'
      });
      return;
    }

    try {
      const payload = {
        receiver_name: form.receiver_name.trim(),
        province: form.province?.trim() || null,
        city: form.city?.trim() || null,
        address: form.address.trim(),
        phone: form.phone.trim(),
        postcode: form.postcode.trim(),
        is_active: form.is_active
      };

      if (editId) {
        await updateWarehouseAddress(editId, payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:warehouseAddress.messages.updated'),
          severity: 'success'
        });
      } else {
        await createWarehouseAddress(payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:warehouseAddress.messages.created'),
          severity: 'success'
        });
      }
      handleClose();
      loadAddresses();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:warehouseAddress.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteWarehouseAddress(deleteDialog.item.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:warehouseAddress.messages.deleted'),
        severity: 'success'
      });
      setDeleteDialog({ open: false, item: null });
      loadAddresses();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:warehouseAddress.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleSetActive = async (sid) => {
    try {
      await setActiveWarehouseAddress(sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:warehouseAddress.messages.setActive'),
        severity: 'success'
      });
      loadAddresses();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:warehouseAddress.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('fulfillment:table.sn'), width: 80, sortable: false, filterable: false },
    {
      field: 'receiver_name',
      headerName: t('fulfillment:warehouseAddress.receiverName'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'address',
      headerName: t('fulfillment:warehouseAddress.address'),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 300 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'province',
      headerName: 'Province',
      width: 140,
    },
    {
      field: 'city',
      headerName: 'City',
      width: 140,
    },
    {
      field: 'phone',
      headerName: t('fulfillment:warehouseAddress.phone'),
      width: 150,
    },
    {
      field: 'postcode',
      headerName: t('fulfillment:warehouseAddress.postcode'),
      width: 120,
    },
    {
      field: 'is_active',
      headerName: t('fulfillment:warehouseAddress.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('fulfillment:warehouseAddress.active') : t('fulfillment:warehouseAddress.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('fulfillment:table.actions'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!params.row.is_active && (
            <Tooltip title={t('fulfillment:warehouseAddress.setActive')}>
              <IconButton
                size="small"
                onClick={() => handleSetActive(params.row.sid)}
                color="success"
              >
                <CheckCircle />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('common:edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              color="error"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('fulfillment:warehouseAddress.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAddresses}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('common:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('fulfillment:warehouseAddress.add')}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {addresses.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', minHeight: 400 }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('fulfillment:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadAddresses}>
                  {t('common:refresh')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(addresses, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1000,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' },
                    borderColor: 'divider'
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none'
                  }
                }}
              />
            )}
          </Box>

        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0 || loading}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('common:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('common:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1 || loading}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('common:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('common:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('common:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('fulfillment:warehouseAddress.edit') : t('fulfillment:warehouseAddress.add')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('fulfillment:warehouseAddress.receiverName')}
                value={form.receiver_name}
                onChange={(e) => setForm({ ...form, receiver_name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Province"
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('fulfillment:warehouseAddress.address')}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label={t('fulfillment:warehouseAddress.phone')}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label={t('fulfillment:warehouseAddress.postcode')}
                value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                required
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                }
                label={t('fulfillment:warehouseAddress.setAsActive')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common:cancel')}</Button>
          <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>{t('common:confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('fulfillment:warehouseAddress.confirmDelete', { name: deleteDialog.item?.receiver_name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseAddress;
